import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import type { ApiError, ApiSuccess, AuthTokens } from '@/types/api';
import { tokenStorage } from './token-storage';

const BASE_URL = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3000/api/v1';

/**
 * Error API yang sudah dinormalkan.
 *
 * Membungkus AxiosError agar komponen tidak perlu menelusuri
 * `error.response.data.errors` di setiap tempat.
 */
export class ApiRequestError extends Error {
  constructor(
    override readonly message: string,
    readonly code: string,
    readonly status: number,
    readonly fieldErrors: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  /** Pesan untuk field tertentu, dipakai react-hook-form. */
  fieldError(field: string): string | undefined {
    return this.fieldErrors[field];
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// --- Request: sisipkan access token ---
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccessToken();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

/**
 * Antrean untuk request yang tertahan saat token sedang di-refresh.
 *
 * Tanpa ini, sepuluh request paralel yang sama-sama kena 401 akan
 * memicu sepuluh panggilan refresh — dan sembilan di antaranya memakai
 * token yang sudah dirotasi, yang di backend terbaca sebagai token
 * reuse dan mencabut seluruh sesi pengguna.
 */
let refreshPromise: Promise<AuthTokens> | null = null;

/** Dipanggil saat sesi benar-benar berakhir; dipasang oleh auth store. */
let onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

/**
 * Dipanggil saat backend menolak request karena sandi baru saja direset
 * Super Admin dan belum diganti — beda dari sesi berakhir: token akses
 * masih sah untuk /auth/change-password, /auth/logout-all, dan /auth/me.
 */
let onPasswordResetRequired: (() => void) | null = null;

export function setPasswordResetRequiredHandler(handler: () => void): void {
  onPasswordResetRequired = handler;
}

/**
 * Dipanggil saat sebuah BACAAN ditolak karena izin kurang.
 *
 * Ada karena kegagalan izin adalah satu-satunya kegagalan yang tidak terlihat.
 * Kesalahan lain berakhir jadi pesan merah di layar, tapi permintaan yang
 * ditolak di latar — pengisi dropdown, misalnya — hanya menghasilkan daftar
 * kosong. Pemakainya melihat layar yang "tidak bisa dipakai" tanpa petunjuk,
 * dan sebabnya cuma bisa ditemukan dari log server. Itu pernah terjadi dan
 * berhari-hari tidak ketahuan.
 *
 * Hanya untuk GET: kegagalan menyimpan sudah punya penanganannya sendiri di
 * tiap formulir, dan dua pesan untuk satu kejadian lebih membingungkan
 * daripada satu.
 */
let onPermissionDenied: ((message: string) => void) | null = null;

export function setPermissionDeniedHandler(handler: (message: string) => void): void {
  onPermissionDenied = handler;
}

async function refreshTokens(): Promise<AuthTokens> {
  const refreshToken = tokenStorage.getRefreshToken();

  if (!refreshToken) {
    throw new Error('Tidak ada refresh token');
  }

  // Instance terpisah: memakai apiClient akan memicu interceptor ini
  // lagi dan menciptakan rekursi tak berujung saat refresh gagal.
  const response = await axios.post<ApiSuccess<AuthTokens>>(
    `${BASE_URL}/auth/refresh`,
    { refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );

  const tokens = response.data.data;
  tokenStorage.setTokens(tokens);

  return tokens;
}

// --- Response: normalisasi error + refresh otomatis ---
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,

  async (error: AxiosError<ApiError>) => {
    const config = error.config as
      (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const status = error.response?.status;
    const code = error.response?.data?.code;

    const canRetry =
      status === 401 &&
      config &&
      !config._retried &&
      // Hanya token kedaluwarsa yang layak dicoba ulang. Kredensial salah
      // atau token dipakai ulang tidak akan membaik dengan refresh.
      code === 'TOKEN_EXPIRED' &&
      Boolean(tokenStorage.getRefreshToken());

    if (canRetry) {
      config._retried = true;

      try {
        refreshPromise ??= refreshTokens().finally(() => {
          refreshPromise = null;
        });

        const tokens = await refreshPromise;
        config.headers.Authorization = `Bearer ${tokens.accessToken}`;

        return apiClient(config);
      } catch {
        tokenStorage.clear();
        onSessionExpired?.();
      }
    }

    if (status === 401 && code !== 'TOKEN_EXPIRED') {
      tokenStorage.clear();
      onSessionExpired?.();
    }

    if (status === 403 && code === 'PASSWORD_RESET_REQUIRED') {
      onPasswordResetRequired?.();
    }

    if (status === 403 && code === 'FORBIDDEN' && config?.method?.toLowerCase() === 'get') {
      onPermissionDenied?.(
        error.response?.data?.message ?? 'Anda tidak memiliki izin atas data ini',
      );
    }

    return Promise.reject(toApiRequestError(error));
  },
);

function toApiRequestError(error: AxiosError<ApiError>): ApiRequestError {
  const data = error.response?.data;

  if (!error.response) {
    return new ApiRequestError(
      'Tidak dapat terhubung ke server. Periksa koneksi internet Anda.',
      'NETWORK_ERROR',
      0,
    );
  }

  const fieldErrors: Record<string, string> = {};
  for (const item of data?.errors ?? []) {
    fieldErrors[item.field] = item.message;
  }

  return new ApiRequestError(
    data?.message ?? 'Terjadi kesalahan pada server',
    data?.code ?? 'INTERNAL_ERROR',
    error.response.status,
    fieldErrors,
  );
}

/** Mengambil `data` dari envelope agar pemanggil bekerja dengan payload langsung. */
export async function unwrap<T>(promise: Promise<AxiosResponse<ApiSuccess<T>>>): Promise<T> {
  const response = await promise;
  return response.data.data;
}
